import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _templateObject;
import React from 'react';
import { Message, Form, Input, Button, Transition, Dimmer, Loader, Divider } from 'semantic-ui-react';
import { withFormik } from 'formik';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import queryString from 'query-string';
import { AuthClient } from '@emyw/core-lib';
import { normalizeErrors } from '../utils';
import ButtonLink from '../ButtonLink';
const {
  REACT_APP_TOKEN_NAME,
  REACT_APP_REFRESH_TOKEN_NAME
} = process.env;
const Wrapper = elements => elements.children.map(element => element);
let verificationCodeSubmitted = false;
const VerifyAccountLogic = withFormik({
  validateOnBlur: false,
  validateOnChange: false,
  // Transform outer props into form values
  mapPropsToValues: () => ({
    email: '',
    verificationCode: ''
  }),
  // Submission handler
  handleSubmit: async (values, _ref) => {
    let {
      props: {
        skipVerification,
        codeEntryMode,
        mutate,
        location,
        onVerificationSuccess,
        autoAuthenticate,
        verificationEmail
      },
      setErrors,
      setStatus,
      setSubmitting,
      setFieldValue,
      status
    } = _ref;
    const {
      verificationCode
    } = values;
    let response;
    // log.trace('submitting', values);
    try {
      // get any verification token from the url
      const verifyToken = queryString.parse((location || {}).search).t;
      if (skipVerification && !verifyToken) {
        // Note that we use a predetermined 'secret' code for verification skip, E1A92
        response = await mutate({
          variables: {
            verifyToken: 'E1A92',
            authenticate: autoAuthenticate
          }
        });
      } else if (codeEntryMode && verificationCode) {
        response = await mutate({
          variables: {
            verifyToken: verificationCode.replace(/^\s+|\s+$/g, ''),
            authenticate: autoAuthenticate,
            verificationEmail
          }
        });
      } else if (!verifyToken) {
        setStatus('noLink');
      } else {
        setStatus('validatingLink');
        response = await mutate({
          variables: {
            verifyToken,
            authenticate: autoAuthenticate,
            verificationEmail: null
          }
        });
      }
      setSubmitting(false);
      // handle the response if one is available from the above verify checks
      if (response) {
        const {
          ok,
          token,
          refreshToken,
          email,
          errors
        } = response.data.validateNewAccountLink;
        if (ok) {
          setFieldValue('email', email);
          if (status !== 'validLink') {
            setStatus('validLink');
            if (autoAuthenticate) {
              localStorage.setItem(REACT_APP_TOKEN_NAME, token);
              localStorage.setItem(REACT_APP_REFRESH_TOKEN_NAME, refreshToken);
            }
            if (onVerificationSuccess) onVerificationSuccess();else log.warn('onVerificationSuccess prop required but not provided');
          }
        } else {
          setStatus('invalidLink');
          setErrors(normalizeErrors(errors, 'general', true));
        }
      }
      verificationCodeSubmitted = false;
    } catch (error) {
      setSubmitting(false);
      setErrors({
        general: [error.message]
      });
    }
  }
});

/** Verify Account Form is intended as a general purpose page for verifying a user's account
 * <br /><br /><hr /><br />
 * <div>
 * <h4>Back-end Requirements</h4>
 * A backend graphql api endpoint with the validateNewAccountLink mutation,
 * <a rel=noopener href="https://github.com/emyw/auth-api">auth-api</a>,
 * is needed for this form to function.
 * <h4>Verify Account</h4>
 * The primary use case is for when a user has created a new account that has not yet been verified
 * by sending an email with the verification link to the user. This link is a reference to a page with
 * this component and contains a verification token. Upon load, this component will check the token
 * in the query string and request verification from the server.<br />
 * If successful, the account will be marked as verified and the component will present a success
 * message to the user.
 * </div>
 */
const VerifyAccountForm = _ref2 => {
  let {
    submitForm,
    status,
    values,
    onLogin,
    onVerificationSuccess,
    onTryAgain,
    handleChange,
    handleSubmit,
    isSubmitting,
    errors,
    forceStatus,
    location,
    codeEntryMode,
    skipVerification
  } = _ref2;
  const localStatus = !status ? forceStatus : status;
  if (skipVerification && (localStatus || 'validatingLink') === 'validatingLink') {
    log.trace('skipping verification', localStatus);
    if (!verificationCodeSubmitted) {
      verificationCodeSubmitted = true;
      setTimeout(submitForm, 100); // avoid update during transition error in console
    }
    return /*#__PURE__*/React.createElement(Dimmer, {
      active: true,
      inverted: true
    }, /*#__PURE__*/React.createElement(Loader, {
      inverted: true
    }, "One Moment..."));
  }
  // When in code entry mode, we require the entry of the code to verify.
  if (codeEntryMode && !queryString.parse((location || {}).search).t) {
    return /*#__PURE__*/React.createElement("div", null, isSubmitting && /*#__PURE__*/React.createElement(Transition, {
      animation: "scale",
      duration: 500
    }, /*#__PURE__*/React.createElement(Dimmer, {
      active: true,
      inverted: true
    }, /*#__PURE__*/React.createElement(Loader, {
      inverted: true
    }, "One Moment..."))), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
      error: !!errors.general
    }, /*#__PURE__*/React.createElement(Input, {
      size: "massive",
      name: "verificationCode",
      id: "verificationCode",
      placeholder: "CODE",
      onChange: handleChange,
      value: values.verificationCode,
      fluid: true
    })), /*#__PURE__*/React.createElement(Divider, {
      hidden: true
    }), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Button, {
      className: "subscribeNavigation square",
      type: "submit",
      disabled: isSubmitting,
      onClick: handleSubmit,
      size: "large",
      primary: true,
      fluid: true,
      style: {
        margin: '0px auto',
        maxWidth: '300px'
      }
    }, "CONTINUE"))), errors.general && /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Divider, {
      hidden: true
    }), /*#__PURE__*/React.createElement(Message, {
      size: "small",
      error: true,
      header: "Trouble verifying account...",
      list: errors.general
    })));
  }

  // When not in codeEntryMode, we look for a resetToken in the query string for verificaton
  // when initializing the page, first we will validate the reset link code
  if (!verificationCodeSubmitted && !localStatus && values.email === '') {
    if (!location) {
      return /*#__PURE__*/React.createElement("div", null, "Error: No location or verification token found");
    }
    // log.trace('submitting', values, location);
    verificationCodeSubmitted = true;
    submitForm();
    // handleSubmit();
  }
  return /*#__PURE__*/React.createElement("div", null, (localStatus === '' || localStatus === 'validatingLink') && /*#__PURE__*/React.createElement("div", null, "Processing Link. One moment please..."), !localStatus && null, (!codeEntryMode && localStatus === 'noLink' || localStatus === 'invalidLink') && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Message, null, /*#__PURE__*/React.createElement(Message.Header, null, "Expired or invalid link"), /*#__PURE__*/React.createElement("p", null, "Oops. Something went wrong. Perhaps this link was already used or has expired."), /*#__PURE__*/React.createElement("div", {
    className: "verificationMessageButton"
  }, /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: () => {
      verificationCodeSubmitted = false;
      if (onTryAgain) onTryAgain();else log.warn('onTryAgain prop required but not provided');
    }
  }, "Try Again?")), errors.general && /*#__PURE__*/React.createElement(Message, {
    size: "mini",
    error: true
  }, errors.general))), localStatus === 'validLink' && !onVerificationSuccess && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Message, null, /*#__PURE__*/React.createElement(Message.Header, null, "Congratulations ", values.email, "!"), /*#__PURE__*/React.createElement("p", null, "Your account has been validated."), onLogin && /*#__PURE__*/React.createElement("div", {
    className: "verificationMessageButton"
  }, /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: () => {
      if (onLogin) onLogin();else log.warn('onLogin prop required but not provided');
    }
  }, "Log In")))), localStatus === 'validLink' && onVerificationSuccess && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Message, null, /*#__PURE__*/React.createElement(Message.Header, null, "Congratulations ", values.email, "!"), /*#__PURE__*/React.createElement("p", null, "Your account has been validated."), /*#__PURE__*/React.createElement("p", null, "One moment please..."))));
};
const validateNewAccountLinkMutation = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    mutation ($verifyToken: String!, $authenticate: Boolean, $verificationEmail: String) {\n        validateNewAccountLink(verifyToken: $verifyToken, authenticate: $authenticate, verificationEmail: $verificationEmail) {\n            ok\n            email\n            token\n            refreshToken\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
VerifyAccountForm.defaultProps = {
  forceStatus: undefined,
  location: undefined,
  autoAuthenticate: false,
  codeEntryMode: false,
  verificationEmail: undefined
};
export default compose(graphql(validateNewAccountLinkMutation, {
  client: AuthClient
}), VerifyAccountLogic)(VerifyAccountForm);